import { useState } from 'react';
import {
  LIBELLES_STATUT,
  STATUTS_SORTIE,
  motifObligatoire,
  transitionsPossibles,
  type CandidatureDetail,
  type ChangementStatut,
  type StatutCandidature,
} from '@rh/shared';
import { appel } from '../api';
import { Erreur } from './Etat';

/**
 * Changement de statut à la main, limité aux étapes permises. L'embauche n'y
 * figure pas : elle passe par la fiche employé ; l'écart non plus depuis le
 * 2026-09-19 : il a son bouton et sa fenêtre de motifs (`Ecarter.tsx`).
 */
export function ChangerStatut({
  candidatureId,
  statut,
  onChange,
}: {
  candidatureId: string;
  statut: StatutCandidature;
  onChange: (detail: CandidatureDetail) => void;
}) {
  const [cible, setCible] = useState<StatutCandidature | ''>('');
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const possibles = transitionsPossibles(statut).filter((s) => s !== 'embauche' && s !== 'ecarte');
  if (possibles.length === 0) return null;

  const avecMotif = cible !== '' && (STATUTS_SORTIE as readonly string[]).includes(cible);
  const motifManquant = cible !== '' && motifObligatoire(cible) && !motif.trim();

  async function valider(): Promise<void> {
    if (!cible) return;
    setErreur(null);
    setEnvoi(true);
    try {
      const corps: ChangementStatut = { statut: cible, motif: avecMotif ? motif.trim() || null : null };
      const detail = await appel<CandidatureDetail>(`/candidatures/${candidatureId}`, {
        method: 'PATCH',
        body: JSON.stringify(corps),
      });
      setCible('');
      setMotif('');
      onChange(detail);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Changement impossible');
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <div className="changer-statut">
      <select
        value={cible}
        aria-label="Changer le statut"
        onChange={(evenement) => setCible(evenement.target.value as StatutCandidature | '')}
      >
        <option value="">Changer le statut…</option>
        {possibles.map((s) => (
          <option key={s} value={s}>
            {LIBELLES_STATUT[s]}
          </option>
        ))}
      </select>
      {avecMotif && (
        <input
          type="text"
          maxLength={500}
          value={motif}
          placeholder={motifObligatoire(cible as StatutCandidature) ? 'Motif (obligatoire)' : 'Motif (facultatif)'}
          aria-label="Motif"
          onChange={(evenement) => setMotif(evenement.target.value)}
        />
      )}
      {cible && (
        <button
          type="button"
          className="bouton"
          disabled={envoi || motifManquant}
          onClick={() => void valider()}
        >
          Valider
        </button>
      )}
      {erreur && <Erreur message={erreur} />}
    </div>
  );
}
